import { SortDescriptor } from '@nextui-org/react';
import {
  Dispatch,
  Key,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { ICubage } from '../../../../interfaces/response/Cubage/ICubage';
import { moneyMask } from '../../../../util/mask';

type Column = {
  key: string;
  name: string;
};

export interface UseLocationCubageProps {
  cubages: ICubage[];
  onRowClick?: (cubage: ICubage) => void;
}

export interface UseLocationCubage {
  handleRowClick: (key: Key) => void;
  sortDescriptor: SortDescriptor;
  setSortDescriptor: Dispatch<SetStateAction<SortDescriptor>>;
  columns: Column[];
  sortedLocationsPrices: ICubage[];
  cellData: (cubage: ICubage, columnKey: string) => number | string;
}

const useLocationCubage = ({
  cubages,
  onRowClick,
}: UseLocationCubageProps): UseLocationCubage => {
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: 'idCubagem',
    direction: 'descending',
  });

  const sortedLocationsPrices = useMemo(() => {
    return [...cubages].sort((a: ICubage, b: ICubage) => {
      const first = a[sortDescriptor.column as keyof ICubage] as number;
      const second = b[sortDescriptor.column as keyof ICubage] as number;

      let cmp = 0;
      if (first < second) cmp = -1;
      if (first > second) cmp = 1;

      return sortDescriptor.direction === 'ascending' ? -cmp : cmp;
    });
  }, [sortDescriptor, cubages]);

  const columns: Column[] = [
    {
      key: 'idCubagem',
      name: 'ID',
    },
    {
      key: 'ean',
      name: 'EAN',
    },
    {
      key: 'descricao',
      name: 'DESCRIÇÃO',
    },
    {
      key: 'altura',
      name: 'ALTURA',
    },
    {
      key: 'largura',
      name: 'LARGURA',
    },
    {
      key: 'profundidade',
      name: 'PROFUNDIDADE',
    },
  ];

  const cellData = useCallback(
    (cubage: ICubage, columnKey: string): number | string => {
      switch (columnKey) {
        case 'idCubagem':
          return cubage.idCubagem;
        case 'ean':
          return cubage.ean.toUpperCase();
        case 'descricao':
          return cubage.descricao.toUpperCase();
        case 'altura':
          return moneyMask(cubage.altura, 'CM', 2);
        case 'largura':
          return moneyMask(cubage.largura, 'CM', 2);
        case 'profundidade':
          return moneyMask(cubage.profundidade, 'CM', 2);
        default:
          return '';
      }
    },
    []
  );

  const handleRowClick = (key: Key): void => {
    const cubage = cubages.find(
      (cubageAux) => cubageAux.idCubagem.toString() === key
    );
    if (onRowClick && cubage) onRowClick(cubage);
  };

  return {
    cellData,
    columns,
    handleRowClick,
    setSortDescriptor,
    sortDescriptor,
    sortedLocationsPrices,
  };
};

export default useLocationCubage;
